import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import type { DataTableInstance } from "../../components/ui-kit/tables/data-table/table-features";

import {
  ClinicalStudies,
  OverallVisitStatus,
  PerPatientStudyStatsSimple,
} from "../../api";
import { DataTable } from "../../components/ui-kit/tables/data-table";
import { DataModel } from "../../models/data-model";
import { uniqueValueOptions } from "../../utils/filter-presets";
import { prettyVisitType } from "./utils";

type OverviewRow = PerPatientStudyStatsSimple & { key: string };

export const OverviewTable = observer(function OverviewTable(props: {
  data: PerPatientStudyStatsSimple[];
  study: ClinicalStudies;
}) {
  const { t } = useLingui();
  const [model] = useState(() => new DataModel<OverviewRow>([]));

  useEffect(() => {
    // Rows are per patient AND visit type — synthesize a unique row key.
    model.updateData(
      props.data.map((item) => ({
        ...item,
        key: `${item.patient_id}|${item.visit_type}`,
      })),
    );
  }, [props.data, model]);

  return (
    <DataTable
      persistState
      model={model}
      itemKeyField="key"
      filterNoun={(count) =>
        t`${plural(count, { one: "visit", other: "visits" })}`
      }
      columns={[
        {
          header: <Trans>Patient ID</Trans>,
          attr: "patient_id",
          filter: { kind: "text", group: t`Identity`, placeholder: t`Search…` },
          cell: ({ item }) => (
            <strong>
              <Link
                to={`/patient/${item.patient_id}/clinical-studies/${props.study}/${item.visit_type}`}
              >
                {item.patient_id}
              </Link>
            </strong>
          ),
        },
        {
          header: <Trans>Visit Type</Trans>,
          cell: ({ item }) => prettyVisitType(item.visit_type),
          attr: "visit_type",
          filter: { kind: "multi", group: t`Visit`, options: visitTypeOptions },
        },
        {
          header: <Trans>Visit Status</Trans>,
          attr: "visit_status",
          type: "enum",
          values: OverallVisitStatus,
          filter: {
            kind: "multi",
            group: t`Visit`,
            options: uniqueValueOptions<OverviewRow>("visit_status"),
          },
          cell: ({ item }) => (
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: item.status_color_hex }}
              />
              <span>{item.visit_status}</span>
            </div>
          ),
        },
      ]}
      rowsPerPage={25}
      readOnly={true}
    />
  );
});

function visitTypeOptions(
  table: DataTableInstance<OverviewRow>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of table.getCoreRowModel().rows) {
    const visitType = row.original.visit_type;
    if (visitType) map.set(visitType, prettyVisitType(visitType));
  }
  return map;
}
